import React, { useState, useEffect } from 'react'

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  })

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent')
    if (!cookieConsent) {
      // Show banner after a small delay for better UX
      setTimeout(() => setIsVisible(true), 1000)
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(cookieConsent)
        setPreferences(savedPreferences)
        // Initialize analytics/marketing based on saved preferences
        if (savedPreferences.analytics && typeof gtag !== 'undefined') {
          gtag('consent', 'update', {
            'analytics_storage': 'granted'
          })
        }
        if (savedPreferences.marketing && typeof gtag !== 'undefined') {
          gtag('consent', 'update', {
            'ad_storage': 'granted'
          })
        }
      } catch (e) {
        console.error('Error parsing cookie preferences:', e)
      }
    }
  }, [])

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    }
    setPreferences(allAccepted)
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted))
    
    // Grant all consents for Google Analytics/Ads
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'functionality_storage': 'granted',
        'personalization_storage': 'granted'
      })
    }
    
    setIsVisible(false)
  }

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    }
    setPreferences(onlyNecessary)
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary))
    
    // Deny all non-necessary consents
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'functionality_storage': 'denied',
        'personalization_storage': 'denied'
      })
    }
    
    setIsVisible(false)
  }

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences))
    
    // Update Google consent based on preferences
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied',
        'ad_storage': preferences.marketing ? 'granted' : 'denied',
        'functionality_storage': preferences.functional ? 'granted' : 'denied',
        'personalization_storage': preferences.functional ? 'granted' : 'denied'
      })
    }
    
    setIsVisible(false)
    setShowPreferences(false)
  }

  const handlePreferenceChange = (type) => {
    if (type === 'necessary') return // Can't change necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }))
  }

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center p-4 pointer-events-none">
      <div className="bg-white border border-gray-200 rounded-lg shadow-2xl max-w-lg w-full pointer-events-auto animate-slide-up">
        {!showPreferences ? (
          <div className="p-6">
            <div className="flex items-start mb-4">
              <div className="bg-brand-green/10 p-2 rounded-full mr-3 flex-shrink-0">
                <svg className="w-5 h-5 text-brand-green" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-brand-green mb-2">Rispettiamo la tua privacy</h3>
                <p className="text-sm text-brand-text/70 leading-relaxed">
                  Utilizziamo cookie per migliorare la tua esperienza, analizzare il traffico e personalizzare i contenuti. 
                  Puoi scegliere quali cookie accettare.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleAcceptAll}
                className="flex-1 bg-brand-green text-white px-4 py-2.5 rounded-md hover:bg-brand-light-green transition-colors font-medium text-sm"
              >
                Accetta tutti
              </button>
              <button
                onClick={handleRejectAll}
                className="flex-1 bg-gray-100 text-brand-text px-4 py-2.5 rounded-md hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                Rifiuta tutti
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="flex-1 border border-brand-green text-brand-green px-4 py-2.5 rounded-md hover:bg-brand-green/5 transition-colors font-medium text-sm"
              >
                Personalizza
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-brand-green">Preferenze Cookie</h3>
              <button
                onClick={() => setShowPreferences(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-brand-text">Cookie Necessari</h4>
                  <p className="text-xs text-brand-text/60">Essenziali per il funzionamento del sito</p>
                </div>
                <div className="bg-gray-100 rounded-full p-1">
                  <div className="bg-brand-green w-5 h-5 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-brand-text">Cookie Analitici</h4>
                  <p className="text-xs text-brand-text/60">Ci aiutano a migliorare il sito</p>
                </div>
                <button
                  onClick={() => handlePreferenceChange('analytics')}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences.analytics ? 'bg-brand-green' : 'bg-gray-200'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.analytics ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-brand-text">Cookie Marketing</h4>
                  <p className="text-xs text-brand-text/60">Per annunci personalizzati</p>
                </div>
                <button
                  onClick={() => handlePreferenceChange('marketing')}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences.marketing ? 'bg-brand-green' : 'bg-gray-200'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.marketing ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-brand-text">Cookie Funzionali</h4>
                  <p className="text-xs text-brand-text/60">Migliorano l'esperienza utente</p>
                </div>
                <button
                  onClick={() => handlePreferenceChange('functional')}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    preferences.functional ? 'bg-brand-green' : 'bg-gray-200'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                    preferences.functional ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleSavePreferences}
                className="flex-1 bg-brand-green text-white px-4 py-2.5 rounded-md hover:bg-brand-light-green transition-colors font-medium text-sm"
              >
                Salva Preferenze
              </button>
              <button
                onClick={() => setShowPreferences(false)}
                className="px-4 py-2.5 text-brand-text hover:bg-gray-50 transition-colors font-medium text-sm rounded-md"
              >
                Annulla
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CookieBanner